import superagent from 'superagent';
import PropTypes from 'prop-types';
import noop from 'shared/lib/noop';
import { DEFAULT_LANGUAGE_CODE, DEFAULT_CURRENCY, HTTP_REQUEST_ABORTED } from 'shared/constants';
import featureFlags from 'shared/lib/featureFlags';

export const methods = ['get', 'post', 'put', 'patch', 'delete'];

export const propTypes = PropTypes.shape(methods.concat(['setToken', 'setData', 'setSocketId', 'addErrorCallback']).reduce((acc, curr) => {
  acc[curr] = PropTypes.func.isRequired;
  return acc;
}, {}));

export default class ApiClient {
  _onErrorCbs = [];
  _token = null;
  _language = DEFAULT_LANGUAGE_CODE;
  _currency = DEFAULT_CURRENCY;
  _socketId = null;
  _data = {};
  _config = {};

  constructor(config = {}) {
    this._config = config;
    this._onErrorCbs = [];
    this._token = null;
    this._socketId = null;
    this._data = {};
    this.redux = {};

    methods.forEach((method) => {
      this[method] = this._method.bind(this, false, method);
      this.redux[method] = this._method.bind(this, true, method);
      this.getFile = this._getFile.bind(this, false);
      this.redux.getFile = this._getFile.bind(this, true);
    });
  }

  setToken = (token) => {
    this._token = token;
    return this;
  };

  setLanguage = (language) => {
    if (language) {
      this._language = language;
    }
    return this;
  };

  setCurrency = (currency) => {
    this._currency = currency || DEFAULT_CURRENCY;
    return this;
  };

  setData = (data) => {
    this._data = { ...data };
    return this;
  };

  setSocketId = (socketId) => {
    this._socketId = socketId;
    return this;
  };

  addErrorCallback = (cb) => {
    this._onErrorCbs.push(cb);
  };

  _formatUrl = (path) => {
    const adjustedPath = path[0] !== '/' ? `/${path}` : path;
    return `${this._config.api.mountPath}${adjustedPath}`;
  };

  _logCall = (title, isReduxCall, method, path, data, params, files, response, error, took = null) => {
    if (!featureFlags.logger) {
      return;
    }

    console.groupCollapsed(`%c ${title}`, 'color: blue');
    console.log('%c isReduxCall', 'color: green;font-weight: bold', isReduxCall);
    console.log('%c method', 'color: green;font-weight: bold', method);
    console.log('%c path', 'color: green;font-weight: bold', path);
    console.log('%c data', 'color: green;font-weight: bold', data);
    console.log('%c params', 'color: green;font-weight: bold', params);
    console.log('%c files', 'color: green;font-weight: bold', files);
    console.log('%c token', 'color: green;font-weight: bold', this._token);
    if (took !== null) {
      console.log('%c response', 'color: green;font-weight: bold', response);
      console.log('%c error', 'color: green;font-weight: bold', error);
      console.log('%c took', 'color: green;font-weight: bold', took);
    }
    console.groupEnd(`%c ${title}`, 'color: blue');
  };

  _logCallStart = (isReduxCall, method, path, data, params, files) => {
    this._logCall('API CLIENT CALL', isReduxCall, method, path, data, params, files);
  };

  _logCallEnd = (isReduxCall, method, path, data, params, files, response, error, took) => {
    this._logCall('API CLIENT RESPONSE', isReduxCall, method, path, data, params, files, response, error, took);
  };

  _method = (isReduxCall, method, path, data = {}, params = {}, files = [], progressCallback = noop, abortCallback = null) => new Promise((resolve, reject) => {
    // if (this._config.developmentMode) {
    this._logCallStart(isReduxCall, method, path, data, params, files);
    // }

    const requestStart = Date.now();
    const request = superagent[method](this._formatUrl(path));
    if (abortCallback !== null) {
      abortCallback(request.abort.bind(request));
    }

    if (method === 'get' && data.io) {
      request.query({
        ...params,
        ...this._data,
        language: this._language,
        currency: this._currency,
        /* socketId: this._socketId */ }); // todo: socketId disabled
    } else {
      request.query({
        ...params,
        ...this._data,
        language: this._language,
        currency: this._currency
      });
    }

    request.set('Accept', 'application/json');

    if (this._token) {
      request.set('Authorization', `Bearer ${this._token}`);
    }

    if (files.length > 0) {
      files.forEach((file) => {
        if (file.name && file.file) {
          request.attach(file.name, file.file);
        } else {
          console.error('Invalid format for file {name, file}', file);
        }
      });

      Object.keys(data).forEach(key => request.field(key, JSON.stringify(data[key])));
      Object.keys(this._data).forEach(key => request.field(key, JSON.stringify(this._data[key])));
      // request.field('socketId', JSON.stringify(this._socketId)); // todo: socketId disabled
      request.on('progress', progressCallback);
      request.on('abort', () => {
        const err = new Error('Request aborted');
        err.status = HTTP_REQUEST_ABORTED;
        reject(err);
      });
    } else {
      request.set('Content-Type', 'application/json');
      request.send({ ...data, ...this._data /* socketId: this._socketId */ }); // todo: socketId disabled
    }

    request.end((err, { body } = {}) => {
      // if (this._config.developmentMode) {
      this._logCallEnd(isReduxCall, method, path, data, params, files, body, err, Date.now() - requestStart);
      // }

      if (err) {
        if (!isReduxCall) {
          this._onErrorCbs.forEach(cb => cb(body || err));
        }
        const errResponse = body || err;
        errResponse.error = errResponse.error || errResponse.message;
        return reject(errResponse);
      }

      return resolve(body);
    });
  });

  _getFile = (isReduxCall, path, params = {}, progressCallback = noop, abortCallback = null) => new Promise((resolve, reject) => {
    // if (this._config.developmentMode) {
    this._logCallStart(isReduxCall, 'GET', path, undefined, params);
    // }

    const requestStart = Date.now();
    const request = superagent.get(this._formatUrl(path));
    if (abortCallback !== null) {
      abortCallback(request.abort.bind(request));
    }

    request.query({
      ...params,
      ...this._data,
      language: this._language,
      currency: this._currency,
      /* socketId: this._socketId */ }); // todo: socketId disabled

    if (this._token) {
      request.set('Authorization', `Bearer ${this._token}`);
    }

    request.on('progress', progressCallback);
    request.on('abort', () => {
      const err = new Error('Request aborted');
      err.status = HTTP_REQUEST_ABORTED;
      reject(err);
    });

    request.send();
    request.responseType('blob');

    request.end((err, { header = {}, body } = {}) => {
      // if (this._config.developmentMode) {
      this._logCallEnd(isReduxCall, 'GET', path, undefined, params, undefined, body, err, Date.now() - requestStart);
      // }

      const filename = header['content-disposition']
        ? decodeURIComponent(header['content-disposition']
          .replace('attachment; filename="', '')
          .replace(/"$/, ''))
        : 'attachment';

      if (err) {
        if (!isReduxCall) {
          this._onErrorCbs.forEach(cb => cb(body || err));
        }
        const errResponse = body || err;
        errResponse.error = errResponse.error || errResponse.message;
        return reject(errResponse);
      }

      return resolve({ filename, content: body });
    });
  });
}
